import { useState, useCallback, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { useApi } from '@/api/useApi';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import { ExpertSettingUpdateRequest } from '@dopingtech/live_expert_bff_api_ts/api';
import { useAppSettings } from '@/zustand/app-settings/store';
import { DocumentTypes, useDocumentUpload } from '@/hooks/use-document-upload';
import { useFeatureFlag } from 'configcat-react';
import { CONFIGCAT_KEYS } from '@/constants/configcat-keys';
import { QUERY_KEYS } from '@/constants/QueryKeys';
import { DetailInfo, NotificationSettings } from './profile-card.types';

import { ProfileHeader, EducationLevel, NotificationToggles, AppearanceToggle, PaymentInfo, BackgroundModeModal, ProfileEditModal, MeetLinkCard } from './components';
import { jsonTryParse } from '@/utility';

const useNotificationSettings = () => {
	const { ExpertSettingControllerApi } = useApi();
	const queryClient = useQueryClient();
	const { data: appSettingsData, setData: setAppSettingsData } = useAppSettings();

	const updateSetting = useMutationProvider(({ key, value }: { key: string; value: ExpertSettingUpdateRequest }) => ExpertSettingControllerApi.updateExpertSetting(key, value), {
		onError: () => queryClient.invalidateQueries(QUERY_KEYS.getExpertSettings)
	});

	useQueryProvider([QUERY_KEYS.getExpertSettings], () => ExpertSettingControllerApi.getExpertSettings(), {
		refetchOnWindowFocus: false,
		select: ({ data }) =>
			data?.data.reduce(
				(acc, setting) => ({
					...acc,
					[setting.key]: setting.value === 'true'
				}),
				{} as NotificationSettings
			),
		onSuccess: notifications => {
			setAppSettingsData({
				...appSettingsData,
				notifications
			});
		}
	});

	const toggleNotification = useCallback(
		async (type: keyof NotificationSettings) => {
			try {
				const currentNotifications = appSettingsData?.notifications || {};
				const newValue = !currentNotifications[type];

				setAppSettingsData({
					...appSettingsData,
					notifications: {
						...currentNotifications,
						[type]: newValue
					}
				});

				await updateSetting.mutateAsync({
					key: type,
					value: { value: newValue.toString() }
				});
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
			} catch (error) {
				const currentNotifications = appSettingsData?.notifications || {};
				setAppSettingsData({
					...appSettingsData,
					notifications: {
						...currentNotifications,
						[type]: !currentNotifications[type]
					}
				});
			}
		},
		[appSettingsData, setAppSettingsData, updateSetting]
	);

	return {
		notifications: appSettingsData?.notifications || {},
		toggleNotification,
		isLoadingNotificationMutation: updateSetting.isLoading
	};
};

const ProfileCard = () => {
	const { value: profileEditModalSettings } = useFeatureFlag(CONFIGCAT_KEYS.PROFILE_EDIT_MODAL_SETTINGS, '{}');

	const { value: isOpenBackgroundMode } = useFeatureFlag(CONFIGCAT_KEYS.IS_OPEN_BACKGROUND_MODE, false);

	const _profileEditModalSettings = useMemo(() => jsonTryParse(profileEditModalSettings), [profileEditModalSettings]);

	const { getInfoAndUploadToS3 } = useDocumentUpload();
	const { FeedbackControllerApi } = useApi();
	const { setData: setAppSettingsData, getData: appSettingsData } = useAppSettings();
	const queryClient = useQueryClient();
	const { notifications, toggleNotification, isLoadingNotificationMutation } = useNotificationSettings();

	const [showBackgroundModeModal, setShowBackgroundModeModal] = useState(false);
	const [showProfileEditModal, setShowProfileEditModal] = useState(false);

	const { value: avatarChange, loading: avatarChangeIsLoading } = useFeatureFlag(CONFIGCAT_KEYS.AVATAR_CHANGE, false);

	const detailInfo = queryClient.getQueryData<{ data: { data: DetailInfo } }>(QUERY_KEYS.getLoggedInExpertDetailInfo)?.data?.data;

	const handleImageUpload = useCallback(
		async (event: React.ChangeEvent<HTMLInputElement>) => {
			const file = event.target.files?.[0];
			if (!file) return;

			await getInfoAndUploadToS3(file, DocumentTypes.IMAGE);
		},
		[getInfoAndUploadToS3]
	);

	const handleBackgroundModeToggle = async () => {
		if (notifications.background_mode) {
			await toggleNotification('background_mode');
		} else {
			setShowBackgroundModeModal(true);
		}
	};

	const handleEditPayment = useCallback(() => {
		setAppSettingsData({ ...appSettingsData, isOpenBankInformation: true });
	}, [appSettingsData, setAppSettingsData]);

	const onCloseBackgroundModeModal = async () => {
		if (isLoadingNotificationMutation) {
			return;
		}
		setShowBackgroundModeModal(false);
		await toggleNotification('background_mode');
	};

	if (!detailInfo?.expert) {
		return null;
	}

	return (
		<div className="profile-card">
			<ProfileHeader
				setShowProfileEditModal={setShowProfileEditModal}
				detailInfo={detailInfo}
				specialization={detailInfo?.specialization?.description}
				avatarChange={avatarChange}
				avatarChangeIsLoading={avatarChangeIsLoading}
				handleImageUpload={handleImageUpload}
			/>

			<EducationLevel educationLevelList={detailInfo?.educationLevelList} />

			<NotificationToggles
				notifications={notifications}
				isOpenBackgroundMode={isOpenBackgroundMode}
				handleBackgroundModeToggle={handleBackgroundModeToggle}
				toggleNotification={toggleNotification}
			/>

			<AppearanceToggle />

			<PaymentInfo
				paymentInfo={detailInfo?.paymentInfo}
				handleEditPayment={handleEditPayment}
			/>

			{detailInfo?.backupRoomInfo && <MeetLinkCard backupRoomInfo={detailInfo?.backupRoomInfo} />}

			<BackgroundModeModal
				showBackgroundModeModal={showBackgroundModeModal}
				onCloseBackgroundModeModal={onCloseBackgroundModeModal}
				setShowBackgroundModeModal={setShowBackgroundModeModal}
			/>

			<ProfileEditModal
				showProfileEditModal={showProfileEditModal}
				setShowProfileEditModal={setShowProfileEditModal}
				userType={detailInfo?.specialization?.name}
				formConfig={_profileEditModalSettings?.[detailInfo?.specialization?.name] || {}}
			/>
		</div>
	);
};

export default ProfileCard;
